import {
  FC,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect
} from 'react';
import clsx from 'clsx';
import { isEmpty, kebabCase } from 'lodash-es';
import { SELECT_TYPE } from '@alf/uikit';
import { Icon } from '@namespace/icons2';
import { AText } from '../../../../../AText';
import { Box } from '../../../../../Box';
import { EmptyState } from '../../../../../EmptyState';
import { IntentType } from '../../../../types';

import styles from './index.module.css';

interface Option {
  value: string | number;
  title: string;
  label?: string;
  iconElement?: ReactNode;
  hasNtf?: boolean;
  ntf?: string;
}

export interface SelectContentProps {
  value?: Option['value'];
  options?: Option[];
  size?: 'xs' | 's' | 'm';
  onClick: (option: Option) => void;
  onClose?: () => void;
  additionSelectContentStyle?: string;
  additionSelectTitleStyle?: string;
  additionSelectOptionStyle?: {
    item?: string;
    active?: string;
    activeTitle?: string;
  };
  intentType?: IntentType;
  selectType?: typeof SELECT_TYPE.ADDITIONAL;
  hasCheck?: boolean;
  emptyText?: string;
  setIsOpen: (open: boolean) => void;
  setSearchValue?: (value: string) => void;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  selectedItem: number;
  setSelectedItem: (index: number) => void;
  dataRole?: string;
  // hasIcon?: boolean;
}

export const SelectContent: FC<SelectContentProps> = ({
  value,
  options = [],
  size = 'xs',
  onClick,
  onClose,
  additionSelectContentStyle = '',
  additionSelectTitleStyle = '',
  additionSelectOptionStyle = { item: '', active: '' },
  intentType,
  selectType,
  hasCheck,
  emptyText,
  setIsOpen,
  setSearchValue,
  inputRef,
  selectedItem,
  setSelectedItem,
  dataRole
}) => {
  const handleBlur = useCallback(() => {
    setIsOpen(false);
    setSelectedItem(-1);
    setSearchValue?.('');
    if (inputRef?.current) {
      inputRef.current.blur();
    }
  }, [inputRef, setIsOpen, setSearchValue, setSelectedItem]);

  const scroll = () => {
    const section = document.querySelector('div[class *= selectedItem]');
    return section?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  useEffect(() => {
    if (options.length === 1) {
      setSelectedItem(0);
    }
  }, [options, setSelectedItem]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' && selectedItem < options.length - 1) {
        event.preventDefault();
        setSelectedItem(selectedItem + 1);
        scroll();
      }

      if (event.key === 'ArrowUp' && selectedItem > 0) {
        event.preventDefault();
        setSelectedItem(selectedItem - 1);
        scroll();
      }

      if ((event.key === 'Enter' || event.key === 'Tab') && selectedItem >= 0) {
        event.preventDefault();
        onClick(options[selectedItem]);
        handleBlur();
      }

      if (event.key === 'Escape') {
        handleBlur();
      }
    },
    [setSelectedItem, selectedItem, options, handleBlur, onClick]
  );
  useEffect(() => {
    const handler = (e: KeyboardEvent<Element>) => handleKeyPress(e);
    document.addEventListener('keydown', handler as unknown as EventListener);
    return () => {
      document.removeEventListener(
        'keydown',
        handler as unknown as EventListener
      );
    };
  }, [handleKeyPress]);

  if (isEmpty(options)) {
    return (
      <EmptyState
        text={emptyText || ''}
        classNames={{ containerClassName: styles.containerClassName }}
      />
    );
  }

  return (
    <Box
      data-role={dataRole && `select-content-${dataRole}`}
      direction="column"
      onKeyDown={(e: KeyboardEvent<HTMLElement>) =>
        handleKeyPress(e as unknown as KeyboardEvent)
      }
      className={clsx([
        styles.root,
        intentType && styles[intentType],
        additionSelectContentStyle && additionSelectContentStyle,
        styles[size]
      ])}
    >
      {options.map((option, index) => {
        const isActive = option?.value === value;
        const handleClick = () => {
          onClick(option);
          onClose?.();
          setIsOpen(false);
        };

        return (
          <Box
            data-role={`option-${kebabCase(String(option.value))}`}
            key={`${option.title}-${option.value}`}
            align="center"
            onClick={handleClick}
            className={clsx([
              styles.option,
              intentType && styles[intentType],
              selectType && styles[selectType],
              additionSelectOptionStyle.item,
              isActive && hasCheck && styles.active,
              isActive && !hasCheck && additionSelectOptionStyle.active,
              index === selectedItem &&
                (additionSelectOptionStyle.active || styles.selectedItem)
            ])}
          >
            {option?.iconElement && option.iconElement}
            <AText
              breed={isActive ? 'B' : 'R'}
              className={clsx([
                additionSelectTitleStyle,
                isActive && additionSelectOptionStyle.activeTitle,
                styles.title,
                option.hasNtf && styles.hasNtf,
                intentType && styles[intentType]
              ])}
            >
              {option.ntf ? `${option.title} ${option.ntf}` : option.title}
            </AText>
            {isActive && hasCheck && (
              <Icon
                name="icons/general/action/done"
                color="state_success"
                size={size === 'xs' ? 's' : size}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
